import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ShoppingCart, User, Sun, Moon } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import USPBanner from './USPBanner';
import Konfigurator from './Konfigurator';

const ShopNavigation = ({ hideKonfigurator = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { cart, notification } = useCart();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setSearchOpen(false); }, [location]);

  // v276-cta-pulse: Header-Cart-Icon pulsiert kurz wenn item_count steigt.
  const prevCountRef = useRef(cart?.item_count || 0);
  const [cartPulse, setCartPulse] = useState(false);
  useEffect(() => {
    const cur = cart?.item_count || 0;
    if (cur > (prevCountRef.current || 0)) {
      setCartPulse(true);
      const t = setTimeout(() => setCartPulse(false), 2600);
      prevCountRef.current = cur;
      return () => clearTimeout(t);
    }
    prevCountRef.current = cur;
  }, [cart?.item_count]);

  // v276-cta-pulse: sobald User irgendeine CTA klickt -> body-Klasse setzen,
  // damit die Puls-Animationen fuer die restliche Session stoppen.
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target && e.target.closest && e.target.closest(
        '.pd-cart .btn-add-cart, .cart-checkout-big, .checkout-submit'
      );
      if (el) {
        try { sessionStorage.setItem('trxl_cta_clicked', '1'); } catch (err) {}
        document.body.classList.add('trxl-cta-clicked');
      }
    };
    try {
      if (sessionStorage.getItem('trxl_cta_clicked') === '1') {
        document.body.classList.add('trxl-cta-clicked');
      }
    } catch (err) {}
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
    }
  };

  return (
    <>
      <USPBanner />
      <nav
        data-testid="shop-navigation"
        className={`shop-nav ${scrolled ? 'scrolled' : ''}`}
      >
        <div className="shop-nav-inner">
          <Link to="/" className="shop-nav-logo" data-testid="shop-nav-logo">
            <img src="/images/trxl-shop-logo.png" alt="TrXL Performance" />
          </Link>

          <div className="shop-nav-links">
            <Link to="/shop" className={location.pathname === '/shop' ? 'active' : ''} data-testid="nav-link-shop">
              Shop
            </Link>
            <a href="https://chiptuning.trxl-performance.de/" target="_blank" rel="noopener noreferrer" data-testid="nav-link-chiptuning">
              Chiptuning
            </a>
            <a href="https://fileservice.trxl-performance.de/" target="_blank" rel="noopener noreferrer" data-testid="nav-link-fileservice">
              Fileservice
            </a>
            <Link to="/kontakt" className={location.pathname === '/kontakt' ? 'active' : ''} data-testid="nav-link-contact">
              Kontakt
            </Link>
          </div>

          <div className="shop-nav-actions">
            <button
              className="shop-nav-btn theme-toggle"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Helles Design' : 'Dunkles Design'}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              className="shop-nav-btn"
              onClick={() => { setSearchOpen(!searchOpen); setMobileOpen(false); }}
              data-testid="nav-search-toggle"
            >
              <Search size={18} />
            </button>

            <Link to={user ? '/mein-konto' : '/auth'} className="shop-nav-btn" data-testid="nav-account-btn">
              <User size={18} />
            </Link>

            <Link to="/cart" className={`shop-nav-btn cart-btn${cartPulse ? ' cart-pulse-once' : ''}`} data-testid="nav-cart-btn">
              <ShoppingCart size={18} />
              {cart.item_count > 0 && (
                <span className="cart-badge" data-testid="cart-badge">{cart.item_count}</span>
              )}
            </Link>

            <button
              className="shop-nav-btn mobile-only"
              onClick={() => { setMobileOpen(!mobileOpen); setSearchOpen(false); }}
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="shop-search-bar" data-testid="shop-search-bar">
            <form onSubmit={handleSearch}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Produkte suchen..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
                data-testid="nav-search-input"
              />
              <button type="submit" data-testid="nav-search-submit">Suchen</button>
            </form>
          </div>
        )}
      </nav>

      {/* Cart notification */}
      {notification && (
        <div className="cart-notification" data-testid="cart-notification">
          {notification}
        </div>
      )}

      {mobileOpen && (
        <div className="shop-mobile-menu" data-testid="shop-mobile-menu">
          <Link to="/shop">Shop</Link>
          <a href="https://chiptuning.trxl-performance.de/" target="_blank" rel="noopener noreferrer">Chiptuning</a>
          <a href="https://fileservice.trxl-performance.de/" target="_blank" rel="noopener noreferrer">Fileservice</a>
          <Link to="/kontakt">Kontakt</Link>
          <Link to={user ? '/mein-konto' : '/auth'}>{user ? 'Mein Konto' : 'Anmelden'}</Link>
          <Link to="/cart">Warenkorb ({cart.item_count})</Link>
        </div>
      )}

      <div className="shop-nav-spacer" />
      {!hideKonfigurator && (
        <div className="shop-nav-konfigurator">
          <Konfigurator compact />
        </div>
      )}
    </>
  );
};

export default ShopNavigation;
